import React, { useState, useMemo } from 'react';
import {
  Filter, ChevronDown, ChevronUp, Download,
} from 'lucide-react';
import { exportToExcel } from '../../utils/excelExport';
import { useAuth } from '../../contexts/AuthContext';
import SortableHeader, { usePagedSortableData, TablePager } from '../SortableTable';
import { formatDate } from '../../utils/dateFormat';
import { EFFECTIVENESS_LABELS, COMPETENCY_SHORT, EFFECTIVENESS_COLOR, scoreLabel } from '../../utils/feedbackScales';
import { exportExcelBtnCls } from '../shared/ExportExcelButton';

// Presentational — a sub-tab of Participation Records: the rows arrive
// already run through the shared filters (search / session /
// date range) owned by ParticipationRecords, which also fetches them. What
// stays here is what only feedback has: the department filter, the score
// aggregation per session, and the Excel export.
//
// The API returns the four feedback scores as integers 1-5 (or null) and this
// component keeps them that way — the wording (feedbackScales.js, shared with
// the employee form and the session detail panel) is applied at render and on
// export only, so relabelling a scale never touches an average.

/** Average of an array of 1–5 scores. */
function avgScore(values) {
  const scores = values.filter(v => v != null);
  if (!scores.length) return null;
  return (scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1);
}

/** Competency gain (post − pre) for one record; null unless both are answered. */
function competencyGain(p) {
  return p?.preCompetency != null && p?.postCompetency != null
    ? p.postCompetency - p.preCompetency
    : null;
}

/** Compute average competency gain (post − pre) across an array of parsed records. */
function avgCompetencyGain(parsedList) {
  const gains = parsedList.map(competencyGain).filter(g => g !== null);
  if (!gains.length) return null;
  return (gains.reduce((a, b) => a + b, 0) / gains.length).toFixed(2);
}

/** Returns true if a comment is meaningful (not blank / N/A). */
function hasRealComment(comment) {
  if (!comment) return false;
  const t = comment.trim().toLowerCase();
  return t.length > 0 && !['na', 'n/a', '-', 'nil', 'none'].includes(t);
}

// ── Sub-component: individual response row ───────────────────────────────────

function ResponseRow({ rec }) {
  const [open, setOpen] = useState(false);
  const { parsed } = rec;
  const preLevel = parsed?.preCompetency ?? null;
  const postLevel = parsed?.postCompetency ?? null;
  const gain = competencyGain(parsed);
  const showComment = hasRealComment(parsed?.comments);

  return (
    <>
      <tr
        className={`hover:bg-slate-50 ${showComment ? 'cursor-pointer' : ''}`}
        onClick={() => showComment && setOpen(o => !o)}
      >
        <td className="px-4 py-3 text-sm text-slate-700 whitespace-nowrap">{rec.email}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{rec.position || '—'}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{rec.department || '—'}</td>
        <td className="px-4 py-3">
          {parsed?.programEffectiveness ? (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${EFFECTIVENESS_COLOR[parsed.programEffectiveness] || 'bg-slate-100 text-slate-600'}`}>
              {scoreLabel(parsed.programEffectiveness, EFFECTIVENESS_LABELS)}
            </span>
          ) : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-3">
          {parsed?.trainerEffectiveness ? (
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${EFFECTIVENESS_COLOR[parsed.trainerEffectiveness] || 'bg-slate-100 text-slate-600'}`}>
              {scoreLabel(parsed.trainerEffectiveness, EFFECTIVENESS_LABELS)}
            </span>
          ) : <span className="text-slate-400">—</span>}
        </td>
        {/* Competency is the mirror image of the effectiveness columns: the bare
            number said nothing about the scale, so it now reads "4/5" with the
            wording on hover — the column is too narrow to print it inline. */}
        <td className="px-4 py-3 text-sm text-slate-600 text-center">
          {preLevel !== null
            ? <span title={scoreLabel(preLevel, COMPETENCY_SHORT)}>{preLevel}/5</span>
            : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-3 text-sm text-slate-600 text-center">
          {postLevel !== null
            ? <span title={scoreLabel(postLevel, COMPETENCY_SHORT)}>{postLevel}/5</span>
            : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-3 text-sm text-center font-semibold">
          {gain !== null ? (
            <span className={gain > 0 ? 'text-emerald-600' : gain < 0 ? 'text-red-500' : 'text-slate-600'}>
              {gain > 0 ? `+${gain}` : gain}
            </span>
          ) : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-3 text-center">
          {showComment
            ? open
              ? <ChevronUp className="w-4 h-4 text-slate-400 mx-auto" />
              : <ChevronDown className="w-4 h-4 text-slate-400 mx-auto" />
            : <span className="text-slate-400">—</span>}
        </td>
      </tr>
      {open && showComment && (
        <tr className="bg-blue-50 border-b border-blue-100">
          <td colSpan={9} className="px-5 py-3 text-sm text-slate-700 italic">
            {parsed.comments}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Sub-component: per-session card ─────────────────────────────────────────

function SessionCard({ trainingId, responses }) {
  const [expanded, setExpanded] = useState(false);
  const trainingName = responses[0]?.trainingName || 'Unknown';
  const sessionDate = responses[0]?.sessionDate || null;
  const parsedList = responses.map(r => r.parsed);

  const progEff = avgScore(parsedList.map(p => p?.programEffectiveness));
  const trainerEff = avgScore(parsedList.map(p => p?.trainerEffectiveness));
  const compGain = avgCompetencyGain(parsedList);

  const { rows, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(responses);

  const dateLabel = sessionDate
    ? formatDate(sessionDate)
    : null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Card header */}
      <button
        className="w-full text-left px-5 py-4 flex items-start justify-between hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex-1 min-w-0 pr-4">
          <h3 className="font-semibold text-slate-800 leading-snug">{trainingName}</h3>
          <div className="flex items-center gap-3 mt-1">
            {dateLabel && <span className="text-xs text-slate-500">{dateLabel}</span>}
            <span className="text-xs text-slate-600">
              {responses.length} response{responses.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Aggregate badges */}
        <div className="flex items-center gap-5 shrink-0">
          {progEff && (
            <div className="text-center">
              <div className="text-xs text-slate-600">Program</div>
              <div className="text-sm font-bold text-emerald-700">{progEff}/5</div>
            </div>
          )}
          {trainerEff && (
            <div className="text-center">
              <div className="text-xs text-slate-600">Trainer</div>
              <div className="text-sm font-bold text-blue-700">{trainerEff}/5</div>
            </div>
          )}
          {compGain !== null && (
            <div className="text-center">
              <div className="text-xs text-slate-600">Avg Gain</div>
              <div className={`text-sm font-bold ${parseFloat(compGain) > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                {parseFloat(compGain) > 0 ? `+${compGain}` : compGain}
              </div>
            </div>
          )}
          {expanded
            ? <ChevronUp className="w-4 h-4 text-slate-400" />
            : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {/* Expanded: individual responses */}
      {expanded && (
        <div className="border-t border-gray-100 table-scroll">
          <table className="w-full text-left">
            <thead className="bg-slate-50">
              <tr className="bg-slate-50">
                <SortableHeader label="Email" sortKey="email" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
                {/* Frozen at session completion; the staff master's
                    current value until then — never asked on the form. */}
                <SortableHeader label="Designation" sortKey="position" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
                <SortableHeader label="Dept" sortKey="department" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
                <SortableHeader label="Program" sortKey="program" sortConfig={sortConfig} onSort={requestSort} accessor={r => r.parsed?.programEffectiveness} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
                <SortableHeader label="Trainer" sortKey="trainer" sortConfig={sortConfig} onSort={requestSort} accessor={r => r.parsed?.trainerEffectiveness} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
                <SortableHeader label="Pre" sortKey="pre" sortConfig={sortConfig} onSort={requestSort} accessor={r => r.parsed?.preCompetency ?? null} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
                <SortableHeader label="Post" sortKey="post" sortConfig={sortConfig} onSort={requestSort} accessor={r => r.parsed?.postCompetency ?? null} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
                <SortableHeader label="Gain" sortKey="gain" sortConfig={sortConfig} onSort={requestSort} accessor={r => competencyGain(r.parsed)} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
                <SortableHeader label="" sortKey="" sortable={false} className="px-4 py-2 text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((rec, i) => <ResponseRow key={i} rec={rec} />)}
            </tbody>
          </table>
          <TablePager
            page={page}
            setPage={setPage}
            total={total}
            className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-slate-50"
          />
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

const TrainingFeedback = ({ records: rawRecords }) => {
  // The one filter specific to feedback — the shared bar has no department.
  const [filterDepartment, setFilterDepartment] = useState('all');

  const { hasPermission } = useAuth();

  // Enrich raw rows with display fields (score → label, session date/title)
  const records = useMemo(() =>
    rawRecords.map(r => ({
      ...r,
      email: (r.email || '').trim(),
      trainingId: r.sessionCode || '',
      position: r.position || '',
      department: r.department || '',
      parsed: {
        programEffectiveness: r.programEffectiveness ?? null,
        trainerEffectiveness: r.trainerEffectiveness ?? null,
        preCompetency: r.preCompetency ?? null,
        postCompetency: r.postCompetency ?? null,
        comments: r.comments || null,
      },
      trainingName: r.courseTitle || 'Unknown',
      sessionDate: r.date ? new Date(r.date) : null,
    })),
    [rawRecords]
  );

  const uniqueDepartments = useMemo(() =>
    ['all', ...new Set(records.map(r => r.department).filter(Boolean).sort())],
    [records]
  );

  const filtered = useMemo(() => {
    if (filterDepartment === 'all') return records;
    return records.filter(r => r.department === filterDepartment);
  }, [records, filterDepartment]);

  // Group by session code, sorted by session date descending
  const sessions = useMemo(() => {
    const groups = {};
    filtered.forEach(r => {
      if (!groups[r.trainingId]) groups[r.trainingId] = [];
      groups[r.trainingId].push(r);
    });
    return Object.entries(groups).sort(([, ra], [, rb]) => {
      const da = ra[0]?.sessionDate;
      const db = rb[0]?.sessionDate;
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db - da;
    });
  }, [filtered]);

  // Overall summary stats
  const stats = useMemo(() => {
    const parsedList = filtered.map(r => r.parsed).filter(Boolean);
    return {
      total: filtered.length,
      unique: new Set(filtered.map(r => r.email)).size,
      avgProgram: avgScore(parsedList.map(p => p.programEffectiveness)),
      avgGain: avgCompetencyGain(parsedList),
    };
  }, [filtered]);

  // Excel export
  const handleExport = () => {
    const rows = filtered.map(r => ({
      'Training Session': r.trainingName,
      'Date': r.sessionDate ? formatDate(r.sessionDate) : '',
      'Email': r.email,
      'Designation': r.position,
      'Department': r.department,
      // Score first, wording second — the export reads like the screen.
      'Program Effectiveness': scoreLabel(r.parsed?.programEffectiveness ?? null, EFFECTIVENESS_LABELS),
      'Trainer Effectiveness': scoreLabel(r.parsed?.trainerEffectiveness ?? null, EFFECTIVENESS_LABELS),
      'Pre-training Competency': scoreLabel(r.parsed?.preCompetency ?? null, COMPETENCY_SHORT),
      'Post-training Competency': scoreLabel(r.parsed?.postCompetency ?? null, COMPETENCY_SHORT),
      'Competency Improvement': competencyGain(r.parsed) ?? '',
      'Comments': r.parsed?.comments || '',
    }));
    exportToExcel(rows, 'Training_Feedback_Report.xlsx');
  };

  // ── Main render ─────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">

      {/* Summary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Total Responses</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Unique Respondents</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{stats.unique}</p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Avg Program Effectiveness</p>
          <p className="text-3xl font-bold text-emerald-700 mt-1">
            {stats.avgProgram ? `${stats.avgProgram}/5` : '—'}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <p className="text-sm text-gray-500">Avg Competency Gain</p>
          <p className={`text-3xl font-bold mt-1 ${stats.avgGain && parseFloat(stats.avgGain) > 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
            {stats.avgGain
              ? parseFloat(stats.avgGain) > 0 ? `+${stats.avgGain}` : stats.avgGain
              : '—'}
          </p>
        </div>
      </div>

      {/* Department filter + export — the sub-tab's own toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="relative w-full sm:w-72">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={filterDepartment}
              onChange={e => setFilterDepartment(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
            >
              {uniqueDepartments.map(d => (
                <option key={d} value={d}>{d === 'all' ? 'All Departments' : d}</option>
              ))}
            </select>
          </div>
          {hasPermission('ld:view') && (
            <button
              onClick={handleExport}
              disabled={filtered.length === 0}
              className={exportExcelBtnCls}
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          )}
        </div>
        {filterDepartment !== 'all' && (
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">Showing {filtered.length} of {records.length} responses</span>
            <button
              onClick={() => setFilterDepartment('all')}
              className="text-sm text-blue-600 hover:text-blue-800 font-medium"
            >
              Clear Department Filter
            </button>
          </div>
        )}
      </div>

      {/* Session cards */}
      {sessions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center text-gray-500">
          No feedback records found.
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map(([trainingId, responses]) => (
            <SessionCard key={trainingId} trainingId={trainingId} responses={responses} />
          ))}
        </div>
      )}

    </div>
  );
};

export default TrainingFeedback;
